/**
 * Pure H OS · Phase D Wave 3 · Knowledge Base.
 * SOPs, guides, FAQs and policies indexed by category.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, SeverityBadge } from "../components/lib";
import { TabBar, ListRow, Drawer } from "../components/lib.overlay";
import { KNOWLEDGE, KnowledgeKind } from "../mocks/knowledgeData";

const KIND_LABEL: Record<KnowledgeKind, string> = {
  guide: "Guide", SOP: "SOP", policy: "Policy", FAQ: "FAQ",
};
function kindSeverity(k: KnowledgeKind): "low" | "medium" | "high" {
  if (k === "SOP" || k === "policy") return "medium";
  return "low";
}

type Tab = "all" | KnowledgeKind;

export default function KnowledgeBase() {
  const [tab, setTab] = useState<Tab>("all");
  const [openId, setOpenId] = useState<string | null>(null);

  const filtered = useMemo(() => (tab === "all" ? KNOWLEDGE : KNOWLEDGE.filter((k) => k.kind === tab)), [tab]);
  const current = openId ? KNOWLEDGE.find((k) => k.id === openId) ?? null : null;

  return (
    <div className="space-y-6" data-testid="knowledge-page">
      <Card testId="kb-filters">
        <TabBar<Tab>
          testId="kb-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",    label: `All ${KNOWLEDGE.length}` },
            { key: "guide",  label: "Guides" },
            { key: "SOP",    label: "SOPs" },
            { key: "policy", label: "Policies" },
            { key: "FAQ",    label: "FAQ" },
          ]}
        />
      </Card>

      <Card padded={false} testId="kb-list">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Knowledge Base" title={`${filtered.length} article${filtered.length === 1 ? "" : "s"}`} />
          <div className="text-xs" style={{ color: "var(--ph-muted)" }}>Institutional memory — how ORVAN operates.</div>
        </div>
        <div className="p-2">
          {filtered.map((k) => (
            <ListRow key={k.id} testId={`kb-${k.id}`}
              onClick={() => setOpenId(k.id)}
              label={
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-[0.14em] text-[10px]" style={{ color: "var(--ph-muted)" }}>{KIND_LABEL[k.kind]}</span>
                  <span>{k.title}</span>
                </span>
              }
              sublabel={<>{k.category} · {k.author} · {k.views} views · updated {k.updatedRelative}</>}
              trailing={<SeverityBadge severity={kindSeverity(k.kind)} />}
            />
          ))}
        </div>
      </Card>

      <Drawer open={!!current} onClose={() => setOpenId(null)} testId="kb-drawer" title={current ? current.title : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader eyebrow={`${KIND_LABEL[current.kind]} · ${current.category}`} title={current.author} />
            <div className="text-sm leading-relaxed" style={{ color: "var(--ph-muted)" }}>{current.summary}</div>
            {current.body && (
              <div className="text-sm leading-relaxed whitespace-pre-wrap pt-2" style={{ color: "var(--ph-text)", borderTop: "1px solid var(--ph-border-subtle)" }} data-testid={`kb-body-${current.id}`}>
                {current.body}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Updated</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.updatedRelative}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Views</div>
                <div className="mt-1 text-sm tabular-nums" style={{ color: "var(--ph-text)" }}>{current.views}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Tags</div>
                <div className="mt-1 text-sm" style={{ color: "var(--ph-text)" }}>{current.tags.join(", ")}</div>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
